/**
 * Ryan O'Dowd
 * 2024-02-20
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useMemo,
  useState,
} from 'react';
import {
  WRINKLES_ROUTE,
  updateUserSettings,
} from '@utils/store/actions';
import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import EmptyScreen from '@components/common/EmptyScreen';
import Icon from '@react-native-vector-icons/material-design-icons';
import SectionList from '@components/common/SectionList';
import Slider from '@react-native-community/slider';
import Text from '@components/common/Text';
import {
  View,
} from 'react-native';
import WidgetBase from '@components/WidgetBase';
import WrinkleChip from '@components/WrinkleChip';
import getStyles from './styles';
import hooks from '@hooks';

const SEE_MORE_INITIAL = 20;
const SEE_MORE_INCREMENT = 10;

const WrinkleRefresh = (props: WrinkleRefreshProps) => {
  const navigation = hooks.useNavigation();
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  const [_isDialogOpen, setIsDialogOpen] = useState(false);

  const srsLevelThreshold = hooks.useTypedSelector((state) => state.userSettings.srs_level_threshold);

  const [completedWrinkleGroups, inProgressWrinkleGroupsUnsorted] = hooks.useGetWrinkles(srsLevelThreshold || 1000, true); // @TODO: does this work as expected if the threshold is null? no...that's why i have 1000 here temporarily
  const totalWrinkles = hooks.useTypedSelector((state) => Object.keys(state.wrinkles).length);
  // @TODO: completed array should be empty now...for this component, putting all in the in-progress array: completedWrinkleGroups = completedWrinkleGroups.sort((a, b) => new Date(a.date_next_srs_level).getTime() - new Date(b.date_next_srs_level).getTime());
  const inProgressWrinkleGroups = inProgressWrinkleGroupsUnsorted.sort((a, b) => Math.min(...a.map((w) => new Date(w.date_next_srs_level).getTime())) - Math.min(...b.map((w) => new Date(w.date_next_srs_level).getTime())));

  const [_maxNumVisibleWrinkleGroups, setMaxNumVisibleWrinkleGroups] = useState(SEE_MORE_INITIAL);

  const wrinkleData: any = useMemo(() => {
    let sections: any = { // @TODO: should this be an array instead of an object? because that's what it's changed to below
      readyNow: {
        key: 'readyNow',
        sortOrder: 1,
        title: 'Ready now', // @TODO: i18n
        data: [{wrinkles: []}],
      },
      /* @TODO:
      thisWeek: {
        key: 'thisWeek',
        sortOrder: 2,
        title: 'Ready this week', // @TODO: i18n
        data: [{wrinkles: []}],
      },
      thisMonth: {
        key: 'thisMonth',
        sortOrder: 3,
        title: 'Later this month', // @TODO: i18n
        data: [{wrinkles: []}],
      },
      */
    };
    [...inProgressWrinkleGroups, ...completedWrinkleGroups].forEach((wrinkleGroup) => {
      sections.readyNow.data[0].wrinkles.push(wrinkleGroup);
      /* @TODO: if uncommented, limit these sections to _maxNumVisibleWrinkleGroups as well
      } else if (new Date() >= dateFnsAdd(new Date(wrinkle.date_next_srs_level), {days: -7})) {
        sections.thisWeek.data[0].wrinkles.push(wrinkle);
      } else if (new Date() >= dateFnsAdd(new Date(wrinkle.date_next_srs_level), {months: -1})) {
        sections.thisMonth.data[0].wrinkles.push(wrinkle);
      */
    });
    sections = Object.values(sections).sort((a: any, b: any) => {
      return a.sortOrder - b.sortOrder;
    });

    return sections;
  }, [inProgressWrinkleGroups, completedWrinkleGroups]);

  const isLevelSliderVisible = useMemo(() => {
    return srsLevelThreshold <= 3 || completedWrinkleGroups.some((wg) => wg.some((w) => w.level_number >= 3)) || inProgressWrinkleGroups.some((wg) => wg.some((w) => w.level_number >= 3));
  }, [inProgressWrinkleGroups, completedWrinkleGroups, srsLevelThreshold]);

  let emptyJsx = null;
  if (wrinkleData.flatMap((section) => section.data.flatMap((d) => d.wrinkles)).length === 0) {
    emptyJsx = (
      <EmptyScreen.Wrapper>
        <EmptyScreen.Text>{i18n('allCaughtUp')}</EmptyScreen.Text>
        {totalWrinkles < 4 &&
          <EmptyScreen.Text>
            {i18n('memorizedVersesWillShowHere')}
          </EmptyScreen.Text>
        }
      </EmptyScreen.Wrapper>
    );
  }

  if (!totalWrinkles && completedWrinkleGroups.length === 0 && inProgressWrinkleGroups.length === 0) {
    emptyJsx = (
      <EmptyScreen.Wrapper>
        <EmptyScreen.Text>
          {i18n('wrinkleRefreshEmptyScreen1')}
        </EmptyScreen.Text>
        <View style={styles.textAndIconWrapper}>
          <EmptyScreen.Text extraStyle={styles.testTubeText}>
            {i18n('wrinkleRefreshEmptyScreen2')}
          </EmptyScreen.Text>
          <Icon style={styles.testTubeIcon} name='test-tube' size={24} color={styles.colors.primaryExtraLight} />
        </View>
      </EmptyScreen.Wrapper>
    );
  }

  const numReadyWrinkleGroups = wrinkleData[0].data[0].wrinkles.length;
  const numWrinkleGroupsVisible = Math.min(numReadyWrinkleGroups, _maxNumVisibleWrinkleGroups);

  const numReadyWrinkles = wrinkleData[0].data[0].wrinkles.reduce((curr, wrinkleGroup) => curr + wrinkleGroup.length, 0);
  const numWrinklesVisible = inProgressWrinkleGroups.slice(0, _maxNumVisibleWrinkleGroups).reduce((curr, wrinkleGroup) => curr + wrinkleGroup.length, 0);

  return (
    <WidgetBase.Container
      layout={props.layout}
      cellSize={props.cellSize}
    >
      <WidgetBase.Header
        title={i18n('wrinkleLevelUpWidgetTitle')}
        onPress={() => navigation.navigate(WRINKLES_ROUTE)}
        rightComponent={!!numReadyWrinkleGroups &&
          <Badge
            style={styles.actionBadge}
            color={styles.colors.accent}
          >
            {numReadyWrinkles}
          </Badge>
        }
        rightIconName='cog'
        onPressRightIcon={() => setIsDialogOpen(true)}
      />
      <WidgetBase.Content>
        {/* @TODO:
        <Button// @TODO: hide if no wrinkles in progress/open for leveling up // @TODO: thi sbutton is more advanced than a v1...don't need it right now, but it would theoretically walk users through leveling up one at a time (or multiple at a time if consecutive verses, etc.)
          mode='contained'
          theme={styles.colors.accent}
          onPress={() => {
            // @TODO: test on all wrinkles in order...ui for option to see the verse before the test
          }}
        >
          Start{/* @TODO: i18n /}
        </Button>
        */}
        {/* @TODO: should this be a flatlist, or do future wrinkles make sense to add here? */}
        {emptyJsx ||
          <React.Fragment>
            <SectionList
              style={styles.noBottomPadding}
              sections={wrinkleData}
              scrollEnabled={false}// removes nested VirtualizedLists error
              keyExtractor={(item) => {
                return `${item.translation_id}-${item.verse_id}`; // @TODO: should be able to just use item.id}
              }}
              renderItem={({item}) => {
                return (
                  <View style={styles.wrinklesWrapper}>
                    {item.wrinkles.slice(0, numWrinkleGroupsVisible).map((wrinkleGroup) => {
                      return (
                        <WrinkleChip
                          key={`${wrinkleGroup[0].translation_id}-${wrinkleGroup[0].verse_id}`}
                          wrinkles={wrinkleGroup}
                          verseNumbersOnly={true}
                        />
                      );
                    })}
                  </View>
                );
              }}
              // @TODO: renderSectionHeader={({section}) => section.data[0].wrinkles.length > 0 && _renderSearchResultsSectionHeader(section.title)}
              renderSectionHeader={() => {}}
            />
            {numReadyWrinkleGroups > numWrinkleGroupsVisible && <Text style={styles.seeMoreText} onPress={() => setMaxNumVisibleWrinkleGroups((curr) => curr + SEE_MORE_INCREMENT)}>{i18n('andMore', numReadyWrinkles - numWrinklesVisible)}</Text>}
          </React.Fragment>
        }
      </WidgetBase.Content>
      <Dialog
        visible={_isDialogOpen}
        onDismiss={() => setIsDialogOpen(false)}
      >
        <Dialog.Title>{i18n('wrinkleLevelUp')}</Dialog.Title>
        <Dialog.Content>
          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>{i18n('whatIsSrs')}</Text>
            <View style={styles.srsInfoWrapper}>
              <Text style={styles.srsInfoText}>{i18n('whatIsSrsExplanation1')}</Text>
              <Text style={styles.srsInfoText}>{i18n('whatIsSrsExplanation2')}</Text>
            </View>
          </View>

          {isLevelSliderVisible &&
            <View style={styles.sectionWrapper}>
              <Text style={styles.sectionTitle}>{i18n('levelsToShow')}</Text>
              <View style={styles.srsInfoWrapper}>
                <Text style={styles.srsInfoText}>{i18n('levelsToShowExplanation')}</Text>
                <View style={styles.sliderAndTextWrapper}>
                  <Slider
                    style={styles.sliderContainer}
                    value={srsLevelThreshold || 10}
                    minimumValue={2}
                    minimumTrackTintColor={styles.colors.primaryLight}
                    maximumValue={10}
                    maximumTrackTintColor={styles.colors.gray}
                    onSlidingComplete={(value) => dispatch(updateUserSettings({srs_level_threshold: value}))}
                    StepMarker={({stepMarked, currentValue}) => {
                      if (!stepMarked) {
                        return null;
                      }
                      return (
                        <View style={styles.thumbContainer}>
                          <Text style={styles.thumbText}>{currentValue === 10 ? '10+' : currentValue}</Text>
                        </View>
                      );
                    }}
                    step={1}
                    thumbTintColor='transparent'
                    tapToSeek={true}
                  />
                </View>
              </View>
            </View>
          }

          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>{i18n('pausingWrinkles')}</Text>
            <View style={styles.srsInfoWrapper}>
              <Text style={styles.srsInfoText}>{i18n('pausingWrinklesExplanation')}</Text>
            </View>
          </View>
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            mode='contained'
            onPress={() => setIsDialogOpen(false)}
          >
            {i18n('close')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </WidgetBase.Container>
  );
};

class WrinkleRefreshProps {
  layout: {
    x: number,
    y: number,
    w: number,
    h: number,
  }; // @TODO: type
  cellSize: number;
};

export default WrinkleRefresh;
